import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  FileText, 
  Calendar, 
  Clock, 
  User, 
  Building2, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Download,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { downloadParteTrabajoePdf } from '@/lib/parteTrabajoePdfUtils';
import { toast } from 'sonner';

interface ParteDetalleData {
  id: string;
  numero_parte: string | null;
  fecha_trabajo: string;
  hora_inicio?: string;
  hora_fin?: string | null;
  descripcion_trabajo: string;
  duracion_total: string | null;
  estado: string;
  tipo_intervencion?: string;
  resultado_intervencion?: string | null;
  motivo_no_resuelto?: string | null;
  materiales_necesarios?: string | null;
  firma_cliente: string | null;
  firma_tecnico?: string | null;
  tecnico?: { nombre: string } | null;
  cliente?: { nombre: string } | null;
  cliente_libre?: string | null;
  orden_trabajo?: { numero_ot: string; titulo: string } | null;
}

interface ParteDetalleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parte: ParteDetalleData | null;
}

const tipoIntervencionLabels: Record<string, string> = {
  revision_sistema_seguridad: 'Revisión Sistema Seguridad',
  averias: 'Averías',
  auditoria: 'Auditoría',
  entrega_materiales: 'Entrega de Materiales',
  otros: 'Otros',
};

const resultadoLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  resuelto: { 
    label: 'Resuelto', 
    icon: <CheckCircle className="h-4 w-4" />, 
    color: 'text-emerald-600' 
  },
  no_resuelto: { 
    label: 'No Resuelto', 
    icon: <XCircle className="h-4 w-4" />, 
    color: 'text-red-600' 
  },
  no_realizado: { 
    label: 'No Realizado', 
    icon: <AlertCircle className="h-4 w-4" />, 
    color: 'text-amber-600' 
  },
};

export const ParteDetalleDialog: React.FC<ParteDetalleDialogProps> = ({
  open,
  onOpenChange,
  parte,
}) => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  if (!parte) return null;

  const clienteNombre = parte.cliente?.nombre || parte.cliente_libre || 'Sin cliente';
  const tecnicoNombre = parte.tecnico?.nombre || 'Sin técnico';
  const resultado = parte.resultado_intervencion 
    ? resultadoLabels[parte.resultado_intervencion] 
    : null;

  const handleIrAlParte = () => {
    onOpenChange(false);
    navigate(`/partes-trabajo?edit=${parte.id}`);
  };

  const handleDescargarPdf = async () => {
    setIsDownloading(true);
    try {
      const parteData = {
        id: parte.id,
        numero_parte: parte.numero_parte,
        fecha_trabajo: parte.fecha_trabajo,
        hora_inicio: parte.hora_inicio || '',
        hora_fin: parte.hora_fin,
        descripcion_trabajo: parte.descripcion_trabajo,
        duracion_total: parte.duracion_total,
        estado: parte.estado,
        tipo_intervencion: parte.tipo_intervencion || 'otros',
        firma_cliente: parte.firma_cliente,
        firma_tecnico: parte.firma_tecnico,
        materiales_necesarios: parte.materiales_necesarios,
        resultado_intervencion: parte.resultado_intervencion,
        motivo_no_resuelto: parte.motivo_no_resuelto,
        empresa_id: (parte as any).empresa_id,
      };
      
      await downloadParteTrabajoePdf(parteData as any, clienteNombre, tecnicoNombre);
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error descargando PDF:', error);
      toast.error('Error al descargar el PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-500" />
              Parte #{parte.numero_parte || 'Sin número'}
            </DialogTitle>
            <Badge
              variant={parte.estado === 'finalizado' ? 'default' : 'secondary'}
            >
              {parte.estado === 'finalizado' ? 'Finalizado' : parte.estado}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* OT asociada */}
          {parte.orden_trabajo && (
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">OT Asociada</p>
              <p className="text-sm font-medium">
                {parte.orden_trabajo.numero_ot} - {parte.orden_trabajo.titulo}
              </p>
            </div>
          )}

          {/* Info principal */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Fecha</p>
                <p className="text-sm font-medium">
                  {format(new Date(parte.fecha_trabajo), 'dd/MM/yyyy', { locale: es })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Horario</p>
                <p className="text-sm font-medium">
                  {parte.hora_inicio} - {parte.hora_fin || '?'}
                  {parte.duracion_total && (
                    <span className="text-muted-foreground"> ({parte.duracion_total})</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Técnico</p>
                <p className="text-sm font-medium">{parte.tecnico?.nombre || 'Sin asignar'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Cliente</p>
                <p className="text-sm font-medium">{clienteNombre}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Tipo de intervención */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Tipo de Intervención</p>
            <Badge variant="outline">
              {tipoIntervencionLabels[parte.tipo_intervencion] || parte.tipo_intervencion}
            </Badge>
          </div>

          {/* Descripción del trabajo */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Descripción del Trabajo</p>
            <p className="text-sm bg-muted/50 p-3 rounded-lg">{parte.descripcion_trabajo}</p>
          </div>

          {/* Materiales */}
          {parte.materiales_necesarios && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Materiales Utilizados</p>
              <p className="text-sm bg-muted/50 p-3 rounded-lg">{parte.materiales_necesarios}</p>
            </div>
          )}

          <Separator />

          {/* Resultado */}
          {resultado && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Resultado de la Intervención</p>
              <div className={`flex items-center gap-2 ${resultado.color}`}>
                {resultado.icon}
                <span className="font-medium">{resultado.label}</span>
              </div>
              {parte.motivo_no_resuelto && (
                <p className="text-sm text-muted-foreground mt-2 bg-muted/50 p-2 rounded">
                  {parte.motivo_no_resuelto}
                </p>
              )}
            </div>
          )}

          {/* Firmas */}
          <div>
            <p className="text-xs text-muted-foreground mb-2">Firmas</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground mb-1">Técnico</p>
                {parte.firma_tecnico ? (
                  <img 
                    src={parte.firma_tecnico} 
                    alt="Firma técnico" 
                    className="h-16 mx-auto object-contain"
                  />
                ) : (
                  <div className="h-16 flex items-center justify-center text-xs text-muted-foreground">
                    Sin firma
                  </div>
                )}
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground mb-1">Cliente</p>
                {parte.firma_cliente ? (
                  <img 
                    src={parte.firma_cliente} 
                    alt="Firma cliente" 
                    className="h-16 mx-auto object-contain"
                  />
                ) : (
                  <div className="h-16 flex items-center justify-center text-xs text-muted-foreground">
                    Sin firma
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={handleDescargarPdf}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Descargar PDF
            </Button>
            <Button 
              variant="default" 
              onClick={handleIrAlParte}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Ir al Parte
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
